import { makeAutoObservable, runInAction } from 'mobx';
import {
  CUSTOMER_TYPE,
  USER_CUSTOMER_STATUSES,
  TWO_FACTOR_AUTH_TYPES,
  CUSTOMER_FEATURES,
  ACCOUNT_NOT_REGISTERED_ERROR_CODE
} from 'components/common/constants';
import { mapWalletResponse } from 'services/utils';
import { base64ToImageUrl } from 'components/utils';
import {
  setSelectedMultiAccount,
  getSelectedMultiAccount,
  setSecureUserPhone,
  setSecureUserEmail,
  setConfirmationActionType,
  setInternalUserId,
  setCustomerId,
  setCustomerType,
  setAvailableConfirmationTypes
} from 'services/authUtils';
import {
  getUserInformation,
  getAccounts,
  getAllAccounts,
  getApplicationState,
  getTariffs,
  getUserInfoByCustomerNumber,
  enableGoogle2FARequest,
  confirmGoogle2FARequest,
  beginVerificationRequest,
  generateSumsubTokenRequest,
  getApplicationConstantsList,
  activateSms2FARequest,
  activateWhatsApp2FARequest,
  disableGoogle2FARequest,
  reactivateGoogle2FARequest,
  downloadAccountDetailsPdfRequest,
  shareWalletRequest,
  unshareWalletRequest,
  registerCustomer
} from 'services/requestAgent';
class UserStore {
  isLoading = false;
  isLoadingAccountDetails = false;
  isInitialized = false;
  isVerificationLoading = false;
  error = null;
  errorAccountDetails = null;
  userData = {};
  userAccounts = [];
  activeWallets = [];
  archivedWallets = [];
  currentAccount = null;
  activeAccountWalletNumber = null;
  currentPagePath = null;
  appFeatures = {};
  appConstants = {};
  appFilterStatuses = [];
  paymentPurposes = [];
  cryptoNetworksBaseUrl = {};
  isCustomerVerified = null;
  isUserDataFetched = false;
  enabledTariffs = [];
  currentCustomerPermissions = null;
  isShowGoogle2FaPopUp = false;
  isGoogle2FaEnablingSuccess = false;
  isGoogle2FaDisablingConfirmation = false;
  isWhatsApp2FaDisablingConfirmation = false;
  isGoogle2FaDisablingSuccess = false;
  isWhatsApp2FaDisablingSuccess = false;
  isGoogle2FAActivated = false;
  isWhatsApp2FAActivated = false;
  isSms2FAActivated = false;
  isPushNotifications2FAActivated = false;
  is2FALoading = false;
  googleQrCode = null;
  confirmationId = null;
  verificationAccessToken = null;
  constructor() {
    makeAutoObservable(this);
  }

  setIsLoading(status) {
    this.isLoading = status;
    this.error = null;
  }

  setIsLoadingAccountDetails(status) {
    this.isLoadingAccountDetails = status;
    this.errorAccountDetails = null;
  }

  setIs2FALoading(status) {
    this.is2FALoading = status;
    this.error = null;
  }

  setIsVerificationLoading(status) {
    this.isVerificationLoading = status;
    this.error = null;
  }

  clearAccountDetailsError() {
    this.errorAccountDetails = null;
  }

  getVerifiedStatus(userData) {
    if (process.env.REACT_APP_KYC_MODE !== '/external') return true;

    if (userData.account?.type === CUSTOMER_TYPE.COMPANY || userData.account?.type === CUSTOMER_TYPE.INDIVIDUAL) {
      return userData.account?.status === USER_CUSTOMER_STATUSES.VERIFIED;
    }
  }

  setCurrentAccount(account) {
    this.currentAccount = account;
  }

  // Opening a wallet (walletNumber truthy) is ignored while a customer switch/account reload is in flight
  // (isLoading) — otherwise a click on a still-rendered card from the customer being switched AWAY from
  // can point this at a wallet_number the new customer doesn't have (see AccountDetails' own self-closing
  // guard for the matching case where it's already pointed at one). Closing (walletNumber === null, e.g.
  // the Back button) is always allowed regardless of isLoading.
  setActiveAccountWalletNumber(walletNumber) {
    if (walletNumber && this.isLoading) return;
    this.activeAccountWalletNumber = walletNumber;
  }

  setCurrentPagePath(path) {
    // Only reset activeAccountWalletNumber if we're actually changing pages
    if (this.currentPagePath && this.currentPagePath !== path) {
      this.activeAccountWalletNumber = null;
    }
    this.currentPagePath = path;
  }

  closeIsShowGoogle2FaPopUp() {
    this.isShowGoogle2FaPopUp = false;
    this.confirmationId = null;
    this.googleQrCode = null;
  }

  setIsGoogle2FaEnablingSuccess(status) {
    this.isGoogle2FaEnablingSuccess = status;
  }

  setIsGoogle2FaDisablingConfirmation(status) {
    this.isGoogle2FaDisablingConfirmation = status;
  }

  setIsWhatsApp2FaDisablingConfirmation(status) {
    this.isWhatsApp2FaDisablingConfirmation = status;
  }

  setIsGoogle2FaDisablingSuccess(status) {
    this.isGoogle2FaDisablingSuccess = status;
  }

  setIsWhatsApp2FaDisablingSuccess(status) {
    this.isWhatsApp2FaDisablingSuccess = status;
  }

  resetEnabledTariffs() {
    this.enabledTariffs = [];
  }

  updateRepresentativeData(updatedRepresentative, isRemove = false) {
    // Guard against undefined or null updatedRepresentative
    if (!updatedRepresentative) {
      console.warn('updateRepresentativeData called with undefined/null representative');
      return;
    }

    const existingRepresentative = this.userData.representatives?.find(({ id }) => id === updatedRepresentative.id);

    this.userData.representatives = existingRepresentative
      ? this.userData.representatives.reduce((result, representative) => {
          if (representative.id === updatedRepresentative.id) {
            if (isRemove) return result;
            result.push(updatedRepresentative);
            return result;
          }
          result.push(representative);

          return result;
        }, [])
      : [...this.userData.representatives, updatedRepresentative];
  }

  getCurrentCustomerPermissions(availableCustomers, customerNumber) {
    return (
      availableCustomers.find(
        (customer) =>
          customer.account_number === customerNumber || customer?.customer_account?.account_number === customerNumber
      )?.permissions || null
    );
  }

  hasPermissions = (expectedPermissions = []) => {
    const userPermissions = this.currentCustomerPermissions;
    // When we don't have permission in account object -> main account
    if (!userPermissions) {
      return true;
    }
    return !!userPermissions?.filter((permission) => expectedPermissions.includes(permission)).length;
  };

  changeCurrentUserAccount = async (customerNumber) => {
    runInAction(() => {
      this.verificationAccessToken = null;
      // TRN-7476: the open wallet details belong to the previous customer — close them, so the new customer's
      // page shows its own dashboard/accounts list instead of a wallet it doesn't have
      this.activeAccountWalletNumber = null;
      this.currentCustomerPermissions = this.getCurrentCustomerPermissions(
        this.userData.availableAccounts,
        customerNumber
      );
    });
    setSelectedMultiAccount(customerNumber);
    await this.loadUserData(customerNumber);
  };

  async getAppState() {
    try {
      this.setIsLoading(true);
      const state = await getApplicationState();
      const constants = await getApplicationConstantsList();
      runInAction(() => {
        this.appConstants = state?.constants;
        this.appFilterStatuses = constants?.filterStatuses || [];
        this.appFeatures = state?.features;
        this.paymentPurposes = state?.paymentPurposes || [];
        this.cryptoNetworksBaseUrl = state?.cryptoNetworksBaseUrl || {};
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
        this.isLoading = false;
      });
      throw err;
    }
  }

  // TRN-7388/TRN-7390 — deliberately thin, no try/catch here unlike every other action in this file:
  // `this.error` is already overloaded to mean "customer-less, show the ChooseAccountType screen" (see the
  // ACCOUNT_NOT_REGISTERED branch in loadUserData() below), so a transient failure of this (e.g.
  // INDIVIDUAL_ACCOUNT_ALREADY_REGISTERED) must stay local to the caller's own state instead of overwriting
  // that unrelated meaning of the same field. ChooseAccountType catches and renders the error itself.
  async registerCustomer(kycProfile) {
    return registerCustomer(kycProfile);
  }

  getCurrentAccount(selectedSharedAccountData, ownUserAccounts) {
    if (selectedSharedAccountData) {
      return selectedSharedAccountData;
    }
    return ownUserAccounts.find(({ type }) => type === CUSTOMER_TYPE.COMPANY) || ownUserAccounts[0];
  }

  // Lightweight, isolated fetch used by the registration flow to gate the "Personal account" option
  // for a logged-in user (e.g. on a full page refresh landing directly on a registration route).
  // Deliberately does not touch isUserDataFetched/isInitialized so it never interferes with the
  // app's real bootstrap in App.js/SuccessAccountRegistration.js.
  async loadAvailableAccountsForRegistrationGate() {
    try {
      const data = await getUserInformation();
      runInAction(() => {
        this.userData = {
          ...this.userData,
          availableAccounts: [...(data.user_accounts || []), ...(data.shared_accounts || [])]
        };
      });
    } catch (err) {
      // best-effort only - the registration gate falls back to its default (no existing accounts) behavior
    }
  }

  async loadUserData(customerNumber, isAfterSuccessfulRegistration) {
    this.setIsLoading(true);
    let data = null;
    let userDataRepresentation = null;

    try {
      this.setIsLoading(true);
      let selectedSharedAccountData = null;
      data = await getUserInformation();
      if (data?.user_info?.phone) {
        setSecureUserPhone(data?.user_info?.phone);
      }
      if (data?.user_info?.email) {
        setSecureUserEmail(data?.user_info?.email);
      }

      // Build basic user data representation early (before any potential errors)
      let availableSharedAccounts = data.shared_accounts || [];

      const selectedCustomerNumber = isAfterSuccessfulRegistration
        ? data.user_accounts[data.user_accounts.length - 1]?.account_number
        : customerNumber || getSelectedMultiAccount() || null;

      // Try to load selected customer, but don't fail if it's not accessible
      if (selectedCustomerNumber) {
        try {
          selectedSharedAccountData = await getUserInfoByCustomerNumber(selectedCustomerNumber);
        } catch (err) {
          // If customer is not accessible (404), clear selected account and continue with first available
          if (err?.response?.status === 404 || err?.status === 404 || err?.code === 404) {
            console.warn('Selected customer is no longer accessible, switching to first available account', {
              selectedCustomerNumber,
              availableAccounts: [...data.user_accounts, ...availableSharedAccounts]
            });
            // Remove the inaccessible account from localStorage
            localStorage.removeItem('selected_multi_account');
            selectedSharedAccountData = null;
            // Filter out the inaccessible account from shared accounts
            availableSharedAccounts = availableSharedAccounts.filter(
              (acc) => acc.account_number !== selectedCustomerNumber
            );
            // Clear any previous error since we're handling it
            runInAction(() => {
              this.error = null;
            });
            // Continue with first available account
          } else {
            // Re-throw other errors
            throw err;
          }
        }
      }

      // DTO changed for UI representation (past representation without multiaccounts)
      userDataRepresentation = {
        account: this.getCurrentAccount(selectedSharedAccountData, data.user_accounts),
        representatives: data.representatives,
        availableAccounts: [...data.user_accounts, ...availableSharedAccounts],
        ...data.user_info
      };

      const currentCustomerPermissions = this.getCurrentCustomerPermissions(
        userDataRepresentation.availableAccounts,
        selectedCustomerNumber
      );

      let accounts = [];
      // Move accounts logic to separate store!
      if (!userDataRepresentation?.account?.account_number) {
        this.error = { code: ACCOUNT_NOT_REGISTERED_ERROR_CODE };
      } else {
        accounts = await getAccounts(userDataRepresentation.account.account_number);
      }

      runInAction(() => {
        setInternalUserId(userDataRepresentation?.user_id);
        setCustomerId(userDataRepresentation?.account?.account_number);
        setCustomerType(userDataRepresentation?.account?.type);
        setConfirmationActionType(userDataRepresentation.two_factor_auth_type);
        if (userDataRepresentation.available_confirmation_types) {
          setAvailableConfirmationTypes(userDataRepresentation.available_confirmation_types);
        }
        this.userData = userDataRepresentation;
        // Only clear error if user has account, preserve ACCOUNT_NOT_REGISTERED error
        if (userDataRepresentation?.account?.account_number) {
          this.error = null; // Clear any previous error on successful load
        }
        this.isGoogle2FAActivated = userDataRepresentation?.two_factor_auth_type === TWO_FACTOR_AUTH_TYPES.GOOGLE_AUTH;
        this.isWhatsApp2FAActivated = userDataRepresentation?.two_factor_auth_type === TWO_FACTOR_AUTH_TYPES.WHATSAPP;
        this.isSms2FAActivated = userDataRepresentation?.two_factor_auth_type === TWO_FACTOR_AUTH_TYPES.SMS;
        this.isPushNotifications2FAActivated =
          userDataRepresentation?.two_factor_auth_type === TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION;
        this.currentCustomerPermissions = currentCustomerPermissions;
        this.userAccounts = accounts;
        this.activeWallets = accounts;
        this.currentAccount = accounts.length > 0 ? accounts[0] : null;
        this.isCustomerVerified = this.getVerifiedStatus(userDataRepresentation);
        this.isUserDataFetched = true;
        this.isInitialized = true;
        this.isLoading = false;
      });

      // Load active wallets (without shared) for transaction forms
      this.loadActiveWallets();
    } catch (err) {
      console.error('Error in loadUserData:', err);

      runInAction(() => {
        // If we have userDataRepresentation, use it despite the error
        if (userDataRepresentation) {
          this.userData = userDataRepresentation;
          this.error = null; // Clear error since we have user data
          this.isUserDataFetched = true;

          // Update auth utils with new customer info
          setInternalUserId(userDataRepresentation?.user_id);
          setCustomerId(userDataRepresentation?.account?.account_number);
          setCustomerType(userDataRepresentation?.account?.type);
          if (userDataRepresentation.two_factor_auth_type) {
            setConfirmationActionType(userDataRepresentation.two_factor_auth_type);
          }
          if (userDataRepresentation.available_confirmation_types) {
            setAvailableConfirmationTypes(userDataRepresentation.available_confirmation_types);
          }

          // Update verification status and permissions
          this.isCustomerVerified = this.getVerifiedStatus(userDataRepresentation);
          this.currentCustomerPermissions = this.getCurrentCustomerPermissions(
            userDataRepresentation.availableAccounts,
            userDataRepresentation.account?.account_number
          );
          // Try to load accounts if we have an account
          if (userDataRepresentation.account?.account_number) {
            getAccounts(userDataRepresentation.account.account_number)
              .then((accounts) => {
                runInAction(() => {
                  this.userAccounts = accounts || [];
                  this.currentAccount = accounts?.[0] || null;
                });
              })
              .catch((e) => console.warn('Failed to load accounts:', e));
          } else {
            this.userAccounts = [];
            this.currentAccount = null;
          }
        }
        // If we at least have basic data from getUserInformation, create minimal userData
        else if (data) {
          const minimalUserData = {
            account: data.user_accounts?.[0] || null,
            representatives: data.representatives || [],
            availableAccounts: [...(data.user_accounts || []), ...(data.shared_accounts || [])],
            ...data.user_info
          };
          this.userData = minimalUserData;
          this.error = null; // Clear error since we recovered
          this.isUserDataFetched = true;

          // Update auth utils with new customer info
          setInternalUserId(minimalUserData?.user_id);
          setCustomerId(minimalUserData?.account?.account_number);
          setCustomerType(minimalUserData?.account?.type);
          if (minimalUserData.two_factor_auth_type) {
            setConfirmationActionType(minimalUserData.two_factor_auth_type);
          }
          if (minimalUserData.available_confirmation_types) {
            setAvailableConfirmationTypes(minimalUserData.available_confirmation_types);
          }

          // Update verification status and permissions
          this.isCustomerVerified = this.getVerifiedStatus(minimalUserData);
          this.currentCustomerPermissions = this.getCurrentCustomerPermissions(
            minimalUserData.availableAccounts,
            minimalUserData.account?.account_number
          );
          // Try to load accounts if we have an account
          if (minimalUserData.account?.account_number) {
            getAccounts(minimalUserData.account.account_number)
              .then((accounts) => {
                runInAction(() => {
                  this.userAccounts = accounts || [];
                  this.currentAccount = accounts?.[0] || null;
                });
              })
              .catch((e) => console.warn('Failed to load accounts:', e));
          } else {
            this.userAccounts = [];
            this.currentAccount = null;
          }
        }
        // Only set error if we have no data at all
        else {
          this.error = err;
          this.userData = {}; // Set empty object to prevent NoOutlet check from failing
          this.isCustomerVerified = false;
          this.currentCustomerPermissions = null;
          this.userAccounts = [];
          this.currentAccount = null;
        }

        this.isInitialized = true;
        this.isLoading = false;
      });
    }
  }

  async loadEnabledTariffs(accountNumber, transactional) {
    try {
      // Move tariffs logic to separate store as well likely
      const data = await getTariffs(this.userData.account?.account_number, accountNumber, transactional);
      runInAction(() => {
        this.enabledTariffs = data;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
      throw err;
    }
  }

  // Temporary solution for updating balances
  async getUserAccounts() {
    try {
      const accounts = await getAccounts(this.userData.account.account_number);
      runInAction(() => {
        this.userAccounts = accounts;
      });
      this.loadActiveWallets();
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
      throw err;
    }
  }

  async loadActiveWallets() {
    try {
      const response = await getAllAccounts(this.userData.account.account_number);
      runInAction(() => {
        if (response.activeWallets) {
          this.activeWallets = response.activeWallets.map((wallet) => mapWalletResponse(wallet));
        }
      });
    } catch (err) {
      // Silently fail - activeWallets will fall back to userAccounts
      console.warn('Failed to load active wallets:', err);
    }
  }

  async loadAllAccountsForFilter() {
    try {
      const response = await getAllAccounts(this.userData.account.account_number);
      runInAction(() => {
        if (response.archivedWallets) {
          this.archivedWallets = response.archivedWallets.map((wallet) => mapWalletResponse(wallet));
        }
        if (response.activeWallets) {
          this.activeWallets = response.activeWallets.map((wallet) => mapWalletResponse(wallet));
        }
      });
    } catch (err) {
      // Silently fail - filters will work with just active wallets
      console.warn('Failed to load all wallets for filter:', err);
    }
  }

  async downloadAccountDetailsPdf(customerNumber, accountNumber) {
    try {
      this.setIsLoadingAccountDetails(true);
      await downloadAccountDetailsPdfRequest(customerNumber, accountNumber);
    } catch (err) {
      runInAction(() => {
        this.errorAccountDetails = err;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoadingAccountDetails = false;
      });
    }
  }

  async shareWallet(customerNumber, walletNumber, accountHolder) {
    try {
      this.setIsLoadingAccountDetails(true);
      const updatedAccount = await shareWalletRequest(customerNumber, walletNumber, accountHolder);

      // Update only the wallet_account_holder field in userAccounts
      const accountIndex = this.userAccounts.findIndex((acc) => acc.wallet_number === walletNumber);
      if (accountIndex !== -1) {
        runInAction(() => {
          // Only update the wallet_account_holder field to preserve all other account data
          this.userAccounts[accountIndex] = {
            ...this.userAccounts[accountIndex],
            wallet_account_holder: updatedAccount?.wallet_account_holder || updatedAccount?.walletAccountHolder
          };
        });
      }

      return updatedAccount;
    } catch (err) {
      runInAction(() => {
        this.errorAccountDetails = err;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoadingAccountDetails = false;
      });
    }
  }

  async unshareWallet(customerNumber, walletNumber) {
    try {
      this.setIsLoadingAccountDetails(true);
      const updatedAccount = await unshareWalletRequest(customerNumber, walletNumber);

      // Update only the wallet_account_holder field in userAccounts
      const accountIndex = this.userAccounts.findIndex((acc) => acc.wallet_number === walletNumber);
      if (accountIndex !== -1) {
        runInAction(() => {
          // Only update the wallet_account_holder field to preserve all other account data
          this.userAccounts[accountIndex] = {
            ...this.userAccounts[accountIndex],
            wallet_account_holder: updatedAccount?.wallet_account_holder || updatedAccount?.walletAccountHolder
          };
        });
      }

      return updatedAccount;
    } catch (err) {
      runInAction(() => {
        this.errorAccountDetails = err;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoadingAccountDetails = false;
      });
    }
  }

  isFeesEnabled() {
    return !!this.appFeatures.tariffsEnabled;
  }

  isCardsEnabled() {
    return !!this.appFeatures.cardsEnabled;
  }

  isRepresentativesEnabled() {
    return !!this.appFeatures.representativesEnabled;
  }

  isNewPaymentFormEnabled() {
    if (!this.userData?.account?.features) {
      return false;
    }
    const feature = this.userData.account.features.find((f) => f.feature === CUSTOMER_FEATURES.NEW_PAYMENT_FORM);
    return feature?.enabled === true;
  }

  isCsvImportEnabled() {
    if (!this.userData?.account?.features) {
      return false;
    }
    const feature = this.userData.account.features.find(
      (f) => f.feature === CUSTOMER_FEATURES.IMPORT_TRANSACTIONS_FROM_CSV
    );
    return feature?.enabled === true;
  }

  isHideConveraApiSepaFpsEnabled() {
    if (!this.userData?.account?.features) {
      return false;
    }
    const feature = this.userData.account.features.find(
      (f) => f.feature === CUSTOMER_FEATURES.HIDE_CONVERA_API_SEPA_FPS
    );
    return feature?.enabled === true;
  }

  getKycProvider() {
    return this.appFeatures.kycProvider;
  }

  getKybProvider() {
    return this.appFeatures.kybProvider;
  }

  async enableGoogle2FA() {
    this.setIs2FALoading(true);
    try {
      const { qr_code, confirmation_uid } = await enableGoogle2FARequest();

      runInAction(() => {
        this.googleQrCode = base64ToImageUrl(qr_code);
        this.confirmationId = confirmation_uid;
        this.isShowGoogle2FaPopUp = true;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.is2FALoading = false;
      });
    }
  }

  async reactivateGoogle2FA() {
    this.setIs2FALoading(true);
    try {
      const { two_factor_auth_type: twoFactorAuthType, available_confirmation_types: availableConfirmationTypes } =
        await reactivateGoogle2FARequest();

      runInAction(() => {
        setConfirmationActionType(twoFactorAuthType);
        setAvailableConfirmationTypes(availableConfirmationTypes);
        this.isWhatsApp2FAActivated = false;
        this.isGoogle2FAActivated = true;
        this.isPushNotifications2FAActivated = false;
        this.isSms2FAActivated = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.is2FALoading = false;
      });
    }
  }

  async confirmGoogle2FAEnabling(code) {
    this.setIs2FALoading(true);
    try {
      const { two_factor_auth_type: twoFactorAuthType, available_confirmation_types: availableConfirmationTypes } =
        await confirmGoogle2FARequest({ confirmation_id: this.confirmationId, code });

      runInAction(() => {
        setConfirmationActionType(twoFactorAuthType);
        setAvailableConfirmationTypes(availableConfirmationTypes);
        this.confirmationId = null;
        this.googleQrCode = null;
        this.isGoogle2FaEnablingSuccess = true;
        this.isShowGoogle2FaPopUp = false;
        this.isGoogle2FAActivated = twoFactorAuthType === TWO_FACTOR_AUTH_TYPES.GOOGLE_AUTH;
        this.isWhatsApp2FAActivated = twoFactorAuthType === TWO_FACTOR_AUTH_TYPES.WHATSAPP;
        this.isPushNotifications2FAActivated = twoFactorAuthType === TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION;
        this.isSms2FAActivated = twoFactorAuthType === TWO_FACTOR_AUTH_TYPES.SMS;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.is2FALoading = false;
      });
    }
  }

  async disableGoogle2FA() {
    this.setIs2FALoading(true);

    try {
      const { two_factor_auth_type: twoFactorAuthType, available_confirmation_types: availableConfirmationTypes } =
        await disableGoogle2FARequest();

      runInAction(() => {
        setConfirmationActionType(twoFactorAuthType);
        setAvailableConfirmationTypes(availableConfirmationTypes);
        this.isGoogle2FaDisablingConfirmation = false;
        this.isGoogle2FaDisablingSuccess = true;
        this.isGoogle2FAActivated = twoFactorAuthType === TWO_FACTOR_AUTH_TYPES.GOOGLE_AUTH;
        this.isWhatsApp2FAActivated = twoFactorAuthType === TWO_FACTOR_AUTH_TYPES.WHATSAPP;
        this.isPushNotifications2FAActivated = twoFactorAuthType === TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION;
        this.isSms2FAActivated = twoFactorAuthType === TWO_FACTOR_AUTH_TYPES.SMS;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.is2FALoading = false;
      });
    }
  }

  async activateWhatsApp2FA() {
    this.setIs2FALoading(true);
    try {
      const { two_factor_auth_type: twoFactorAuthType, available_confirmation_types: availableConfirmationTypes } =
        await activateWhatsApp2FARequest();

      runInAction(() => {
        setConfirmationActionType(twoFactorAuthType);
        setAvailableConfirmationTypes(availableConfirmationTypes);
        this.isGoogle2FAActivated = false;
        this.isPushNotifications2FAActivated = false;
        this.isSms2FAActivated = false;
        this.isWhatsApp2FAActivated = true;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.is2FALoading = false;
      });
    }
  }

  async activateSms2FA() {
    this.setIs2FALoading(true);

    try {
      const { two_factor_auth_type: twoFactorAuthType, available_confirmation_types: availableConfirmationTypes } =
        await activateSms2FARequest();

      runInAction(() => {
        setConfirmationActionType(twoFactorAuthType);
        setAvailableConfirmationTypes(availableConfirmationTypes);
        this.isWhatsApp2FAActivated = false;
        this.isGoogle2FAActivated = false;
        this.isPushNotifications2FAActivated = false;
        this.isSms2FAActivated = true;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.is2FALoading = false;
      });
    }
  }

  async beginVerification(customerNumber, data) {
    this.setIsVerificationLoading(true);
    try {
      const { token: accessToken, amlStatus } = await beginVerificationRequest(customerNumber, data);

      runInAction(() => {
        this.verificationAccessToken = accessToken ? accessToken : null;

        if (this.userData?.account) {
          this.userData.account.amlLevelName = data?.level_name;
          this.userData.account.amlStatus = amlStatus ? amlStatus : null;
        }
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.isVerificationLoading = false;
      });
    }
  }

  async generateSumsubToken(customerNumber, levelName) {
    this.setIsVerificationLoading(true);
    try {
      const { token } = await generateSumsubTokenRequest(customerNumber, levelName);

      runInAction(() => {
        this.verificationAccessToken = token ? token : null;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.isVerificationLoading = false;
      });
    }
  }

  getPaymentPurposesByProviderAndCountry(providerType, countryCode) {
    if (!providerType || !countryCode || !this.paymentPurposes?.length) {
      return null;
    }

    const providerData = this.paymentPurposes.find((item) => item.transferProvider === providerType);

    if (!providerData || !providerData.paymentPurposes) {
      return null;
    }

    return providerData.paymentPurposes[countryCode] || null;
  }
}

export default new UserStore();
